import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import ConversationList from "../components/chat/ConversationList";
import MessagesList from "../components/chat/MessagesList";
import { useSelector } from "react-redux";
import "../../css/chat.css";
import {
  getChatMessageList,
  deleteConversation,
  deleteConversationAll,
  deleteMessageApi,
} from "../../services/UserServices/UserService";
import PageTitle from "../layouts/PageTitle";
// import { toast } from "react-toastify";
import { notifyError, notifyTopRight } from "../components/Common/Toaster";
import ConfirmModalLock from "../components/Modal/ConfirmModalLock";
function Chat() {
  const location = useLocation();
  const userId = location?.state?.data;
  const gender = location?.state?.gender;
  const userType = location?.state?.userType;

  const [messageList, setMessageList] = useState([]);
  const selectedConversation = useSelector(
    (state) => state.session.selectedConversationDetails
  );
  const [isChatFetching, setIsChatFetching] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [screenWidth] = useState(window.innerWidth);
  const [selectedConversationIds, setSelectedConversationIds] = useState([]);
  const [refreshListTrigger, setRefreshListTrigger] = useState(false);
  const [showDeleteConversationsConfirm, setShowDeleteConversationsConfirm] =
    useState(false);
  const [isDeletingConversations, setIsDeletingConversations] = useState(false);
  const activeConversationRef = useRef(null);

  const getChatMessages = useCallback(async (conversationId) => {
    activeConversationRef.current = conversationId;
    setMessageList([]);
    setIsChatFetching(true);
    try {
      const response = await getChatMessageList(conversationId);
      if (activeConversationRef.current !== conversationId) return;
      setMessageList(response.data?.data.reverse());
    } catch (error) {
      if (activeConversationRef.current === conversationId) {
        console.log(error, "error");
      }
    } finally {
      if (activeConversationRef.current === conversationId) {
        setIsChatFetching(false);
      }
    }
  }, []);

  const deleteSelectedConversations = async () => {
    if (selectedConversationIds.length === 0 && !isSelectAll) {
      notifyError("Please select at least one conversation to delete.");
      return;
    }

    setIsDeletingConversations(true);
    try {
      if (isSelectAll) {
        const response = await deleteConversationAll(userId);
        setMessageList([]);
        notifyTopRight(response.data.message);
        setIsSelectAll(false);
        setRefreshListTrigger((prev) => !prev);
      } else {
        const payload = { conversationIds: selectedConversationIds };
        const response = await deleteConversation(payload);
        notifyTopRight(response.data.message);
        setSelectedConversationIds([]); // Clear selection after deletion
        if (selectedConversation?.conversationId) {
          getChatMessages(selectedConversation?.conversationId);
        }
        setRefreshListTrigger((prev) => !prev);
      }
      setShowDeleteConversationsConfirm(false);
    } catch (error) {
      console.log(error, "error");
      notifyError("Failed to delete conversations.");
    } finally {
      setIsDeletingConversations(false);
    }
  };

  const handleDeleteConversations = () => {
    if (selectedConversationIds.length === 0 && !isSelectAll) {
      notifyError("Please select at least one conversation to delete.");
      return;
    }
    setShowDeleteConversationsConfirm(true);
  };

  const handleDeleteMessage = async (messageId) => {
    if (!messageId) return;
    try {
      await deleteMessageApi(messageId);
      setMessageList((prev) => prev.filter((m) => m._id !== messageId));
      notifyTopRight("Message deleted successfully.");
    } catch (error) {
      console.error(error);
      notifyError("Failed to delete message.");
    }
  };

  useLayoutEffect(() => {
    document.body.classList.add("no-scroll");
    return () => document.body.classList.remove("no-scroll");
  }, []);

  useEffect(() => {
    if (selectedConversation?.conversationId) {
      getChatMessages(selectedConversation.conversationId);
    } else {
      activeConversationRef.current = null;
      setMessageList([]);
    }
  }, [selectedConversation?.conversationId, getChatMessages]);

  return (
    <>
      <ConfirmModalLock
        open={showDeleteConversationsConfirm}
        title="Delete Conversations"
        description={
          isSelectAll
            ? "Are you sure you want to delete ALL conversations for this user?"
            : `Are you sure you want to delete ${selectedConversationIds.length} selected conversation?`
        }
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeletingConversations}
        onCancel={() => {
          if (isDeletingConversations) return;
          setShowDeleteConversationsConfirm(false);
        }}
        onConfirm={deleteSelectedConversations}
      />
      {location?.state?.type === "details" && userType === "user" ? (
        <PageTitle
          activeMenu={location?.state?.userName}
          motherMenu="User Details"
          previousPath={"/user-details"}
          stateData={location?.state?.selectedTab}
          stateDataPage={location?.state?.page}
          search={location?.state?.search}
          data={userId}
        />
      ) : userType === "user" ? (
        <PageTitle
          activeMenu={location?.state?.userName}
          motherMenu="User Management"
          previousPath={"/user-management"}
          stateData={location?.state?.selectedTab}
          stateDataPage={location?.state?.page}
          search={location?.state?.search}
        />
      ) : location?.state?.type === "details" && userType === "member" ? (
        <PageTitle
          activeMenu={location?.state?.userName}
          motherMenu="Members Details"
          previousPath={"/member-details"}
          stateData={location?.state?.selectedTab}
          stateDataPage={location?.state?.page}
          search={location?.state?.search}
          data={userId}
        />
      ) : location?.state?.type === "details" && userType === "face" ? (
        <PageTitle
          activeMenu={location?.state?.userName}
          motherMenu="Verification User Details"
          previousPath={"/verification-user-details"}
          stateData={location?.state?.selectedTab}
          stateDataPage={location?.state?.page}
          search={location?.state?.search}
          data={userId}
        />
      ) : (
        <PageTitle
          activeMenu={location?.state?.userName}
          motherMenu="Members Management"
          previousPath={"/members"}
          stateData={location?.state?.selectedTab}
          stateDataPage={location?.state?.page}
          search={location?.state?.search}
        />
      )}
      <div className="chat-page">
        {screenWidth > 500 ? (
          <div className="tabs-container">
            <div className="conversation-column">
              <div className="d-flex justify-content-end mb-2">
                {(selectedConversationIds.length > 0 || isSelectAll) && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={handleDeleteConversations}
                  >
                    Delete Selected (
                    {isSelectAll ? "All" : selectedConversationIds.length})
                  </button>
                )}
              </div>
              <ConversationList
                type="appointment"
                userId={userId}
                gender={gender}
                selectedConversationIds={selectedConversationIds}
                setSelectedConversationIds={setSelectedConversationIds}
                refreshConversationList={getChatMessages}
                refreshListTrigger={refreshListTrigger}
                setIsSelectAll={setIsSelectAll}
                isSelectAll={isSelectAll}
              />
            </div>
          </div>
        ) : (
          <div className="">
            {!selectedConversation && (
              <div className="tabs-container">
                <div className="conversation-column">
                  <div className="d-flex justify-content-end mb-2">
                    {(selectedConversationIds.length > 0 || isSelectAll) && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={handleDeleteConversations}
                      >
                        Delete Selected (
                        {isSelectAll ? "All" : selectedConversationIds.length})
                      </button>
                    )}
                  </div>
                  <ConversationList
                    type="appointment"
                    userId={userId}
                    gender={gender}
                    selectedConversationIds={selectedConversationIds}
                    setSelectedConversationIds={setSelectedConversationIds}
                    refreshConversationList={getChatMessages}
                    refreshListTrigger={refreshListTrigger}
                    setIsSelectAll={setIsSelectAll}
                    isSelectAll={isSelectAll}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {screenWidth > 500 ? (
          <MessagesList
            messageList={messageList}
            isChatFetching={isChatFetching}
            conversationSelectedTrue={selectedConversation}
            userId={userId}
            userName={location?.state?.userName}
            onDeleteMessage={handleDeleteMessage}
          />
        ) : (
          <div className="mobile-placeholder">
            {selectedConversation && (
              <MessagesList
                messageList={messageList}
                isChatFetching={isChatFetching}
                conversationSelectedTrue={selectedConversation}
                userId={userId}
                userName={location?.state?.userName}
                onDeleteMessage={handleDeleteMessage}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default Chat;
